import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Copy, Check, ExternalLink, Mail, KeyRound, UserPlus } from 'lucide-react';
import api from '../../lib/api';
import toast from 'react-hot-toast';

const PRACTICE_TYPES = ['wahlarzt', 'therapeut', 'physio', 'psycho', 'sonstiges'];

/**
 * Superadmin-only modal to create a new customer manually.
 * Two states:
 *   1. form   — fill fields, POST /api/superadmin/customers
 *   2. success — show Stripe Payment Link + credentials
 */
export default function NewCustomerModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    practice_name: '',
    first_name: '',
    last_name: '',
    email: '',
    practice_type: 'wahlarzt',
    plan: 'starter',
    billing_cycle: 'monthly',
    setup_fee: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); }

  async function submit(e) {
    e.preventDefault();
    const errs = {};
    if (!form.practice_name.trim()) errs.practice_name = t('common.required');
    if (!form.first_name.trim()) errs.first_name = t('common.required');
    if (!form.last_name.trim()) errs.last_name = t('common.required');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = t('common.invalid');
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const r = await api.post('/api/superadmin/customers', {
        practice_name: form.practice_name.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        practice_type: form.practice_type,
        plan: form.plan,
        billing_cycle: form.billing_cycle,
        setup_fee: !!form.setup_fee,
      });
      setResult(r.data);
      onCreated && onCreated(r.data);
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === 'email_in_use') setErrors({ email: t('revenue.newCustomer.errors.emailInUse') });
      else toast.error(err.response?.data?.message || t('revenue.newCustomer.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-text/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="new-customer-modal-backdrop"
    >
      <div
        className="so-card max-w-lg w-full p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-customer-modal"
        role="dialog"
        aria-modal="true"
      >
        {!result ? (
          <>
            <div className="flex items-start justify-between px-5 py-4 border-b border-border">
              <div>
                <h3 className="text-base font-semibold text-navy flex items-center gap-2">
                  <UserPlus size={16}/> {t('revenue.newCustomer.title')}
                </h3>
                <p className="text-xs text-text-secondary mt-1">{t('revenue.newCustomer.subtitle')}</p>
              </div>
              <button type="button" onClick={onClose} className="text-text-secondary hover:text-text" data-testid="new-customer-close">
                <X size={18}/>
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3">
              <Field
                label={t('revenue.newCustomer.fields.practiceName')}
                error={errors.practice_name}
                input={
                  <input type="text" className="so-input" value={form.practice_name} onChange={(e) => set('practice_name', e.target.value)} data-testid="nc-practice-name" autoFocus/>
                }
              />
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('revenue.newCustomer.fields.firstName')} error={errors.first_name}
                  input={<input className="so-input" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="nc-first-name"/>}/>
                <Field label={t('revenue.newCustomer.fields.lastName')} error={errors.last_name}
                  input={<input className="so-input" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="nc-last-name"/>}/>
              </div>
              <Field label={t('revenue.newCustomer.fields.email')} error={errors.email}
                input={<input type="email" className="so-input" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="nc-email"/>}/>
              <Field label={t('revenue.newCustomer.fields.practiceType')}
                input={
                  <select className="so-input" value={form.practice_type} onChange={(e) => set('practice_type', e.target.value)} data-testid="nc-practice-type">
                    {PRACTICE_TYPES.map((tt) => (<option key={tt} value={tt}>{t(`revenue.newCustomer.types.${tt}`)}</option>))}
                  </select>
                }/>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('revenue.newCustomer.fields.plan')}
                  input={
                    <select className="so-input" value={form.plan} onChange={(e) => set('plan', e.target.value)} data-testid="nc-plan">
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                    </select>
                  }/>
                <Field label={t('revenue.newCustomer.fields.billingCycle')}
                  input={
                    <div className="inline-flex rounded-full bg-border/40 p-0.5 text-xs w-full">
                      {['monthly', 'annual'].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => set('billing_cycle', c)}
                          className={`flex-1 px-3 py-1.5 rounded-full transition-colors ${form.billing_cycle === c ? 'bg-white text-navy shadow-sm font-medium' : 'text-text-secondary'}`}
                          data-testid={`nc-billing-${c}`}
                        >
                          {t(`revenue.newCustomer.cycle.${c}`)}
                        </button>
                      ))}
                    </div>
                  }/>
              </div>
              <label className="flex items-start gap-3 rounded-md border border-border bg-background/60 p-3 cursor-pointer" data-testid="nc-setup-fee-label">
                <input type="checkbox" checked={form.setup_fee} onChange={(e) => set('setup_fee', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border text-accent focus:ring-accent" data-testid="nc-setup-fee-checkbox"/>
                <span className="text-sm text-text">{t('revenue.newCustomer.fields.setupFee')}</span>
              </label>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="so-btn so-btn-secondary">{t('common.cancel')}</button>
                <button type="submit" className="so-btn so-btn-primary" disabled={submitting} data-testid="nc-submit">
                  {submitting ? t('revenue.newCustomer.submitting') : t('revenue.newCustomer.submit')}
                </button>
              </div>
            </form>
          </>
        ) : (
          <SuccessPanel result={result} onClose={onClose} t={t}/>
        )}
      </div>
    </div>
  );
}

function Field({ label, error, input }) {
  return (
    <div>
      <label className="so-label">{label}</label>
      {input}
      {error && <p className="text-xs text-danger mt-1">{error}</p>}
    </div>
  );
}

function SuccessPanel({ result, onClose, t }) {
  const [copiedKey, setCopiedKey] = useState(null);
  function copy(key, value) {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    });
  }
  const emailOk = result.email_status === 'sent' || result.email_status === 'logged_mock';
  return (
    <div className="p-5" data-testid="new-customer-success">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center rounded-full bg-success/15 text-success w-9 h-9 flex-shrink-0">
          <Check size={18}/>
        </span>
        <div>
          <h3 className="text-base font-semibold text-navy">{t('revenue.newCustomer.success.title')}</h3>
          <p className="text-xs text-text-secondary mt-1">
            {t('revenue.newCustomer.success.subtitle', { name: result.practice?.name })}
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-md border border-border p-3 bg-background/60">
        <p className="text-[11px] uppercase tracking-wider text-text-secondary mb-1">
          {t('revenue.newCustomer.success.paymentLinkLabel')}
        </p>
        <p className="text-xs font-mono break-all text-text" data-testid="nc-payment-link-url">{result.payment_link?.url}</p>
        <div className="mt-2 flex gap-2">
          <button onClick={() => copy('link', result.payment_link?.url)} className="so-btn so-btn-secondary text-xs" data-testid="nc-copy-link">
            {copiedKey === 'link' ? <><Check size={12}/> {t('revenue.newCustomer.success.copied')}</> : <><Copy size={12}/> {t('revenue.newCustomer.success.copyButton')}</>}
          </button>
          <a href={result.payment_link?.url} target="_blank" rel="noopener noreferrer" className="so-btn so-btn-secondary text-xs" data-testid="nc-open-link">
            <ExternalLink size={12}/> {t('revenue.newCustomer.success.openLink')}
          </a>
        </div>
      </div>

      <div className="mt-3 rounded-md border border-border p-3 bg-background/60">
        <p className="text-[11px] uppercase tracking-wider text-text-secondary mb-2">
          {t('revenue.newCustomer.success.credentialsLabel')}
        </p>
        <CredentialRow icon={<Mail size={12}/>} label={t('revenue.newCustomer.success.emailLabel')} value={result.user?.email} copied={copiedKey === 'email'} onCopy={() => copy('email', result.user?.email)} testIdPrefix="nc-email"/>
        <CredentialRow icon={<KeyRound size={12}/>} label={t('revenue.newCustomer.success.tempPasswordLabel')} value={result.temp_password} copied={copiedKey === 'pw'} onCopy={() => copy('pw', result.temp_password)} testIdPrefix="nc-temp-pw"/>
      </div>

      <p className={`mt-3 text-xs ${emailOk ? 'text-success' : 'text-danger'}`} data-testid="nc-email-status">
        {emailOk
          ? t('revenue.newCustomer.success.emailSent', { status: result.email_status })
          : t('revenue.newCustomer.success.emailFailed')}
      </p>

      <div className="mt-5 flex justify-end">
        <button onClick={onClose} className="so-btn so-btn-primary" data-testid="nc-success-close">
          {t('revenue.newCustomer.success.closeButton')}
        </button>
      </div>
    </div>
  );
}

function CredentialRow({ icon, label, value, copied, onCopy, testIdPrefix }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <span className="text-xs text-text-secondary flex items-center gap-1.5">{icon} {label}</span>
      <span className="text-xs font-mono text-text break-all" data-testid={`${testIdPrefix}-value`}>{value}</span>
      <button onClick={onCopy} className="text-text-secondary hover:text-text" data-testid={`${testIdPrefix}-copy`} aria-label="copy">
        {copied ? <Check size={12} className="text-success"/> : <Copy size={12}/>}
      </button>
    </div>
  );
}
